/**
 * The current firm's branding (`GET /firms/settings`; US-003) for the SHELL — the accent
 * colour on the app root and the F-29 logo mark in the top bar. One query key shared with
 * the firm-settings screen, so a logo upload / branding save there (which invalidates
 * `['firm', 'settings']`) refreshes the shell in the same tick — which is also why it can
 * be `STALE_REFERENCE_MS` fresh here: the only writer invalidates it, so the shell never
 * needs to poll it (it used to be re-read on every window focus past 30 s).
 *
 * Gated on `firm.read` (ENG-10): a role without it never sends the request, so non-admins
 * get the default brand — never a 403 in the shell. Query is deduped by TanStack, so every
 * shell consumer (AppShell, TopNav) shares a single request.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { api, type FirmSettings } from '../lib/api.ts';
import { usePermission } from '../lib/permissions/index.tsx';
import { STALE_REFERENCE_MS } from '../lib/query-client.ts';

export const FIRM_SETTINGS_QUERY_KEY = ['firm', 'settings'] as const;

export function useFirmSettings(): UseQueryResult<FirmSettings> {
  const canReadFirm = usePermission('firm.read');
  return useQuery<FirmSettings>({
    queryKey: FIRM_SETTINGS_QUERY_KEY,
    queryFn: () => api.firms.getSettings(),
    enabled: canReadFirm,
    staleTime: STALE_REFERENCE_MS,
  });
}
